import React, { useState, useEffect, useRef } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
    Collapse,
    Table,
    TableBody,
    TableContainer,
    Tooltip,
} from '@mui/material';
import {
    ExpandMore,
    ExpandLess,
    Add,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DraggablePlantRow from './DraggablePlantRow';
import { Plant, Space } from '../../types/shared';

interface SpaceDropZoneProps {
    space: Space;
    plants: Plant[];
    onEditPlant: (plant: Plant) => void;
    onRemovePlant?: (plant: Plant) => void;
    onAddPlantToSpace?: (spaceId: string) => void;
    onTransplant?: (plant: Plant) => void;
    isUnassigned?: boolean;
    onImageClick?: (imageUrl: string) => void;
}

const SpaceDropZone: React.FC<SpaceDropZoneProps> = ({
    space,
    plants,
    onEditPlant,
    onRemovePlant,
    onAddPlantToSpace,
    onTransplant,
    isUnassigned = false,
    onImageClick
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(plants.length > 0);
    const prevPlantCount = useRef(plants.length);

    useEffect(() => {
        // Auto-expand if plants are added to an empty space
        if (prevPlantCount.current === 0 && plants.length > 0) {
            setExpanded(true);
        }
        prevPlantCount.current = plants.length;
    }, [plants.length]);

    // Make the content area a drop target for plants
    const { setNodeRef: setDroppableRef, isOver } = useDroppable({
        id: isUnassigned ? 'unassigned-drop' : `${space.id}-drop`,
        data: {
            type: 'space-drop',
            spaceId: isUnassigned ? null : space.id,
        },
    });

    const plantIds = plants.map(p => p.id);

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                bgcolor: isOver ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(5px)',
                mb: 2,
                border: isOver ? '2px dashed' : '1px solid',
                borderColor: isOver ? 'primary.main' : 'divider',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'border-color 0.2s, background-color 0.2s',
            }}
        >
            {/* Space Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    borderBottom: expanded ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: isUnassigned ? 'grey.100' : 'rgba(76, 175, 80, 0.05)',
                }}
            >


                <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                        {isUnassigned ? t('plants.unassigned_plants') : space.name}
                    </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                    {plants.length === 0 ? t('plants.count_plants_zero') :
                        plants.length === 1 ? t('plants.count_plants', { count: 1 }) :
                            t('plants.count_plants_plural', { count: plants.length })}
                </Typography>

                {!isUnassigned && onAddPlantToSpace && (
                    <Tooltip title={t('plants.add_plant_tooltip')}>
                        <IconButton size="small" onClick={() => onAddPlantToSpace(space.id)} sx={{ color: 'primary.main' }}>
                            <Add fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}



                <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </Box>

            {/* Plants List */}
            <Collapse in={expanded}>
                <CardContent
                    ref={setDroppableRef}
                    sx={{
                        p: 0,
                        minHeight: 60,
                        bgcolor: isOver ? 'rgba(76, 175, 80, 0.04)' : 'transparent',
                        '&:last-child': { pb: 0 },
                    }}
                >
                    <SortableContext items={plantIds} strategy={verticalListSortingStrategy}>
                        {plants.length > 0 ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableBody>
                                        {plants.map(plant => (
                                            <DraggablePlantRow
                                                key={plant.id}
                                                plant={plant}
                                                space={space}
                                                onEditPlant={onEditPlant}
                                                onRemovePlant={onRemovePlant}
                                                onTransplant={onTransplant}
                                                onImageClick={onImageClick}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{
                                py: 4,
                                m: 2,
                                textAlign: 'center',
                                color: 'text.secondary',
                                border: '1px dashed',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}>
                                <Typography variant="body2">
                                    {isOver ? t('plants.drop_plant_here') : t('plants.no_plants_in_space')}
                                </Typography>
                            </Box>
                        )}
                    </SortableContext>


                </CardContent>
            </Collapse>
        </Card>
    );
};

export default SpaceDropZone;
